import Phaser from 'phaser';
import { calculateScale } from '../../helpers';
import eventEmitter, { GameEvents } from '../../pages/Home/eventEmiter';
import { ProfileResponseT, UpgradeCharacterResponse } from '../../types';
import { AviatorAnimationsUnion } from '../types';

interface AviatorPhaserSpine extends SpineGameObject {
  isFlyingObject?: boolean
}
export class UpgradeScene extends Phaser.Scene {
  constructor() {
    super('UpgradeScene');
  }

  create() {
    let rocket: AviatorPhaserSpine;
    let smoke: AviatorPhaserSpine;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    this.game.renderer.pipelines.imageSmoothingQuality = 'high';
    this.cameras.main.roundPixels = false;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    this.scale.resolution = window.devicePixelRatio;
    this.cameras.main.setRoundPixels(false);

    const setAnimation = (char: AviatorPhaserSpine, animation: AviatorAnimationsUnion, loop = false, track = 0) => {
      char.state.setAnimation(track, animation, loop);
    };

    // eslint-disable-next-line prefer-const
    rocket = this.add.spine(
      this.game.scale.width / 2,
      this.game.scale.height * 1.1,
      'aviatorSkins',
      'Flying',
      true,
    );
    // eslint-disable-next-line prefer-const
    smoke = this.add.spine(
      this.game.scale.width / 2,
      this.game.scale.height * 0.8,
      'aviatorSkins',
      'Smoke',
      false,
    );
    smoke.visible = false;

    rocket.setScale(calculateScale(rocket, 250, 480));
    rocket.setDepth(1);
    smoke.setDepth(2);
    setAnimation(rocket, 'Idle_On_The_Ground', true);

    const setSkin = (activeParts: ProfileResponseT['character_progress']['active_parts']) => {
      const skeletonData = rocket?.skeleton?.data;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const newSkin = new skeletonData.defaultSkin.constructor('customSkin');

      const baseSkin = skeletonData.findSkin('Base/Body');
      if (baseSkin) {
        newSkin.addSkin(baseSkin);
      }

      activeParts.forEach((skinName) => {
        const attachmentSkin = skeletonData.findSkin(skinName);
        if (attachmentSkin) {
          newSkin.addSkin(attachmentSkin);
        }
      });

      rocket.skeleton.setSkin(newSkin);
      rocket.skeleton.setSlotsToSetupPose();
    };

    const getUpgradeAnimation = (unlockedItem: UpgradeCharacterResponse['unlocked_item']): AviatorAnimationsUnion | null => {
      switch (unlockedItem.slot) {
        case 'Head':
          return 'Single Item Upgraded_Head';
        case 'Jetpack':
          return 'Single Item Upgraded_Jetpack';
        case 'Rocket':
          return 'Single Item Upgraded_Rocket';
        case 'Pant':
          return 'Single Item Upgraded_Pant';
        case 'Shirt':
          return 'Single Item Upgraded_Shirt';
        case 'Shoe':
          return 'Single Item Upgraded_Shoe';
        case 'Body':
          return null;
        default:
          return null;
      }
    };

    const upgradeSkin = (upgradeData: UpgradeCharacterResponse, setNewBaseSkin: boolean) => {
      smoke.visible = true;
      if (!setNewBaseSkin) {
        smoke.state.setAnimation(0, 'Smoke', false);
      }
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const onComplete = () => {
        const animationName = getUpgradeAnimation(upgradeData.unlocked_item);
        if (animationName) {
          setAnimation(rocket, animationName);
          rocket.once('complete', () => {
            if (!upgradeData.full_upgrade) {
              setAnimation(rocket, 'Idle_On_The_Ground', true);
            }
          });
        }
        setSkin(upgradeData.active_parts);
        if (upgradeData.full_upgrade) {
          setTimeout(() => {
            setAnimation(rocket, 'Fly_To_The_Moon', false);
          }, 1000);
        }
      };

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      smoke.once('complete', onComplete);

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      rocket.state.addListener({
        complete: (entry) => {
          if (entry.animation.name === 'Fly_To_The_Moon') {
            eventEmitter.emit(GameEvents.ON_FLY_TO_THE_MOON_OVER);
            setTimeout(() => {
              setAnimation(rocket, 'Landing_On_The_Ground', false);
              rocket.once('complete', () => {
                eventEmitter.emit(GameEvents.ON_LANDING_END);
                setAnimation(rocket, 'Idle_On_The_Ground', true);
              });
            }, 700);
          }
        },
      });
      if (setNewBaseSkin) {
        setAnimation(rocket, 'Upgraded_Full', false);
        rocket.once('complete', () => {
          setSkin(upgradeData.active_parts);
          setAnimation(rocket, 'Idle_On_The_Ground', true);
        });
      }
    };

    eventEmitter.on(GameEvents.SET_CHARACTER_PROGRESS, ({ characterProgress }: { characterProgress: ProfileResponseT['character_progress'] }) => {
      setSkin(characterProgress.active_parts);
    });
    eventEmitter.on(GameEvents.SET_NEW_CHARACTER_ITEM, ({ upgradeData, setNewBaseSkin }: { upgradeData: UpgradeCharacterResponse, setNewBaseSkin: boolean }) => {
      upgradeSkin(upgradeData, setNewBaseSkin);
    });
    eventEmitter.emit(GameEvents.UPGRADE_SCENE_LOADED);
    this.events.on('shutdown', () => {
      eventEmitter.off(GameEvents.SET_CHARACTER_PROGRESS);
      eventEmitter.off(GameEvents.SET_NEW_CHARACTER_ITEM);
      this.children.removeAll();
    });
  }
}
